import React from "react";
import { CheckCircle2 } from "lucide-react";

import Button from "src/components/app/Button";
import {
  getBenefitActions,
  isBenefitAvailed,
  isMilestoneLocked,
  needsMilestoneDocuments,
} from "src/constants/milestones";
import {
  Milestone,
  MilestoneBenefit,
  MilestoneBenefitAction,
} from "src/types/program.types";

// One benefit inside a milestone card — used by both the program details
// screen and the home card so a benefit looks the same wherever it appears.
const BenefitRow: React.FC<{
  milestone: Milestone;
  benefit: MilestoneBenefit;
  onAction: (action: MilestoneBenefitAction) => void;
}> = ({ milestone, benefit, onAction }) => {
  const isLocked = isMilestoneLocked(milestone);
  const docsRequired = needsMilestoneDocuments(milestone);
  const isGated = isLocked || docsRequired;
  // A milestone in the AVAILED state marks every benefit under it as availed,
  // whatever the per-benefit flags say.
  const isAvailed = isBenefitAvailed(milestone, benefit);
  // Claimability doesn't depend on this being the "current" milestone — a past
  // milestone's unclaimed benefit (e.g. M1's free consult while the user is on
  // M4) is still claimable, as long as its documents are cleared.
  const isClaimable = !isGated && !isAvailed;
  // One button per action, exactly as the API sends them.
  const actions = isClaimable ? getBenefitActions(benefit) : [];

  return (
    <div
      className="rounded-xl p-3"
      style={{
        backgroundColor: isGated
          ? "rgba(0, 0, 0, 0.03)"
          : "rgba(var(--app-primary-rgb), 0.05)",
        border: isGated
          ? "1px solid var(--card-border)"
          : "1px solid rgba(var(--app-primary-rgb), 0.15)",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <h4
          className="text-sm font-semibold"
          style={{
            color: isGated
              ? "var(--disabled-text-color)"
              : "var(--card-title-color)",
          }}
        >
          {benefit.title}
        </h4>

        {!isGated && isAvailed ? (
          <div
            className="flex-shrink-0 flex items-center gap-1 text-xs font-medium"
            style={{ color: "var(--status-success)" }}
          >
            <CheckCircle2 className="w-4 h-4" />
            Availed
          </div>
        ) : null}
      </div>

      <p
        className="text-sm mt-1"
        style={{
          color: isGated
            ? "var(--disabled-text-color)"
            : "var(--card-desc-color)",
        }}
      >
        {benefit.description}
      </p>

      {actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {actions.map((action) => (
            <Button
              key={`${action.type}-${action.label}`}
              size="sm"
              color="primary"
              onClick={() => onAction(action)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BenefitRow;
